import React, { useState } from 'react';
import { useTheme } from '../context/ThemeContext';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import './Calendar.css';

const DAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const INIT_EVENTS = [
  { id:1,  date:'2026-05-27', title:'Follow-up: Anand Kumar',   type:'followup',  time:'10:00' },
  { id:2,  date:'2026-05-27', title:'Team standup',             type:'meeting',   time:'09:30' },
  { id:3,  date:'2026-05-28', title:'Follow-up: Deepak Singh',  type:'followup',  time:'11:00' },
  { id:4,  date:'2026-05-28', title:'Demo: Rajesh Nair',        type:'demo',      time:'14:00' },
  { id:5,  date:'2026-05-29', title:'Follow-up: Priya Sharma',  type:'followup',  time:'10:30' },
  { id:6,  date:'2026-05-30', title:'Finance review',           type:'internal',  time:'15:00' },
  { id:7,  date:'2026-05-30', title:'Follow-up: Sreelatha K',   type:'followup',  time:'11:30' },
  { id:8,  date:'2026-06-01', title:'Team performance review',  type:'internal',  time:'10:00' },
  { id:9,  date:'2026-06-03', title:'Follow-up: Vikram Nanda',  type:'followup',  time:'14:00' },
  { id:10, date:'2026-06-05', title:'Invoice due: Rajesh Nair', type:'invoice',   time:'EOD'   },
  { id:11, date:'2026-06-10', title:'Invoice due: Vikram Nanda',type:'invoice',   time:'EOD'   },
  { id:12, date:'2026-05-24', title:'Overdue: Anand Kumar',     type:'overdue',   time:'EOD'   },
];

const TYPE_COLOR = {
  followup: '#F5A623', meeting: '#3B82F6', demo: '#22C55E',
  internal: '#8B5CF6', invoice: '#EC4899', overdue: '#EF4444',
};

const CloseIcon = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>;
const PlusIcon  = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>;
const ChevL     = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>;
const ChevR     = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="9 18 15 12 9 6"/></svg>;

function getDaysInMonth(y, m) {
  return new Date(y, m + 1, 0).getDate();
}
function getFirstDay(y, m) {
  return new Date(y, m, 1).getDay();
}

export default function CalendarPage() {
  const { theme } = useTheme();
  const now = new Date();
  const [year, setYear]   = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());

  // Load events from localStorage + sync from leads and finance
  const [events, setEvents] = useState(() => {
    try {
      const saved = localStorage.getItem('ryzenos_calendar_events');
      const base = saved ? JSON.parse(saved) : INIT_EVENTS;

      // Pull follow-ups from leads
      const leads = JSON.parse(localStorage.getItem('ryzenos_leads') || '[]');
      const leadEvents = leads.filter(l => l.followUp).map(l => ({
        id: 'lead-' + l.id,
        date: l.followUp,
        title: `Follow-up: ${l.name}`,
        type: 'followup',
        time: '10:00',
        auto: true,
      }));

      // Pull due dates from finance invoices
      const invoices = JSON.parse(localStorage.getItem('ryzenos_invoices') || '[]');
      const invoiceEvents = invoices.filter(i => i.due).map(i => ({
        id: 'inv-' + i.id,
        date: i.due,
        title: `Invoice due: ${i.client}`,
        type: 'invoice',
        time: '09:00',
        auto: true,
      }));

      // Merge -- remove old auto events, add fresh ones
      const manual = base.filter(e => !e.auto);
      const allIds = new Set([...leadEvents, ...invoiceEvents].map(e => e.id));
      return [...manual, ...leadEvents, ...invoiceEvents];
    } catch { return INIT_EVENTS; }
  });

  React.useEffect(() => {
    try {
      const manual = events.filter(e => !e.auto);
      localStorage.setItem('ryzenos_calendar_events', JSON.stringify(manual));
    } catch {}
  }, [events]);

  const [selected, setSelected] = useState(null);
  const [showAdd, setShowAdd]   = useState(false);
  const [form, setForm] = useState({ title:'', type:'meeting', time:'10:00' });
  const setF = (k,v) => setForm(f=>({...f,[k]:v}));

  const prevMonth = () => { if(month===0){setMonth(11);setYear(y=>y-1);}else setMonth(m=>m-1); };
  const nextMonth = () => { if(month===11){setMonth(0);setYear(y=>y+1);}else setMonth(m=>m+1); };

  const daysInMonth = getDaysInMonth(year, month);
  const firstDay    = getFirstDay(year, month);
  const todayStr    = now.toISOString().split('T')[0];

  const eventsForDate = (d) => {
    const str = `${year}-${String(month+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
    return events.filter(e => e.date === str);
  };

  const selectedEvents = selected ? events.filter(e => e.date === selected) : [];

  const addEvent = () => {
    if (!form.title.trim() || !selected) return;
    setEvents(ev => [...ev, { id: Date.now(), date: selected, ...form }]);
    setForm({ title:'', type:'meeting', time:'10:00' });
    setShowAdd(false);
  };

  const TopActions = (
    <div className="cal-nav">
      <button className="cal-nav-btn" onClick={prevMonth}><ChevL /></button>
      <span className="cal-nav-title">{MONTHS[month]} {year}</span>
      <button className="cal-nav-btn" onClick={nextMonth}><ChevR /></button>
    </div>
  );

  return (
    <div className="cal-wrap" data-theme={theme}>
      <Sidebar />
      <div className="cal-main">
        <Topbar title="Calendar" subtitle="Scheduled follow-ups, meetings & invoices" actions={TopActions} />

        <div className="cal-body">
          {/* Grid */}
          <div className="cal-grid-wrap">
            <div className="cal-day-headers">
              {DAYS.map(d => <div key={d} className="cal-day-hdr">{d}</div>)}
            </div>
            <div className="cal-grid">
              {Array.from({length: firstDay}).map((_,i) => <div key={'e'+i} className="cal-cell cal-cell--empty"/>)}
              {Array.from({length: daysInMonth}).map((_,i) => {
                const d = i+1;
                const dateStr = `${year}-${String(month+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
                const dayEvents = eventsForDate(d);
                const isToday = dateStr === todayStr;
                const isSelected = dateStr === selected;
                return (
                  <div key={d} className={`cal-cell${isToday?' today':''}${isSelected?' selected':''}`} onClick={() => setSelected(dateStr)}>
                    <div className="cal-cell-num">{d}</div>
                    <div className="cal-cell-events">
                      {dayEvents.slice(0,2).map(ev => (
                        <div key={ev.id} className="cal-ev-pill" style={{background: TYPE_COLOR[ev.type]+'22', borderLeft:`2px solid ${TYPE_COLOR[ev.type]}`}}>
                          <span style={{color: TYPE_COLOR[ev.type]}}>{ev.time !== 'EOD' ? ev.time : ''} {ev.title.split(':')[0]}</span>
                        </div>
                      ))}
                      {dayEvents.length > 2 && <div className="cal-ev-more">+{dayEvents.length-2} more</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Side panel */}
          <div className="cal-side">
            <div className="cal-side-hd">
              <span className="cal-side-title">
                {selected ? new Date(selected+'T12:00:00').toLocaleDateString('en-IN',{weekday:'long',day:'numeric',month:'long'}) : 'Select a day'}
              </span>
              {selected && <button className="cal-add-btn" onClick={()=>setShowAdd(true)}><PlusIcon /></button>}
            </div>

            {showAdd && selected && (
              <div className="cal-add-form">
                <input className="cal-inp" placeholder="Event title" value={form.title} onChange={e=>setF('title',e.target.value)} />
                <div className="cal-form-row">
                  <select className="cal-inp cal-sel" value={form.type} onChange={e=>setF('type',e.target.value)}>
                    {Object.keys(TYPE_COLOR).map(t=><option key={t} value={t}>{t.charAt(0).toUpperCase()+t.slice(1)}</option>)}
                  </select>
                  <input className="cal-inp" type="time" value={form.time} onChange={e=>setF('time',e.target.value)} />
                </div>
                <div className="cal-form-btns">
                  <button className="cal-btn-sec" onClick={()=>setShowAdd(false)}>Cancel</button>
                  <button className="cal-btn-pri" onClick={addEvent}>Add</button>
                </div>
              </div>
            )}

            <div className="cal-events-list">
              {selectedEvents.length === 0 && <div className="cal-empty">{selected ? 'No events' : 'Click a date to view events'}</div>}
              {selectedEvents.map(ev => (
                <div key={ev.id} className="cal-ev-item" style={{borderLeft:`3px solid ${TYPE_COLOR[ev.type]}`}}>
                  <div className="cal-ev-time">{ev.time}</div>
                  <div className="cal-ev-title">{ev.title}</div>
                  <div className="cal-ev-type" style={{color: TYPE_COLOR[ev.type]}}>{ev.type}</div>
                </div>
              ))}
            </div>

            {/* Legend */}
            <div className="cal-legend">
              {Object.entries(TYPE_COLOR).map(([t,c])=>(
                <div key={t} className="cal-leg-item">
                  <div className="cal-leg-dot" style={{background:c}}/>
                  <span>{t}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
